import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import {
  powerDistributionCloneForCreate,
  powerDistributionCreateDraft,
  powerDistributionEnsureNameForCreate,
} from './subsections/powerDistributionEngineBindings';
import { PowerDistributionEngineEditFormBody } from './subsections/PowerDistributionEngineEditFormBody';
import {
  isPowerDistributionPersistedProps,
  type PowerDistributionProps,
  type PowerDistributionPersistedProps,
  type PowerDistributionInitialProps,
} from './usePowerDistribution';
import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { buildLegacyPowerDistributionFromRequestFields } from '../../../components/powerdistribution/powerDistributionListUtils';
import { useRequestScope } from '../../../contexts/RequestScopeContext';
import { usePowerDistributionConfigurations } from '../../../hooks/queries/usePowerDistributionConfigurations';
import {
  descriptorInstanceToPowerDistribution,
  powerDistributionToDescriptorInstance,
} from '../../../services/descriptorEditSessionFlush';
import { shouldShowFacilityPowerAdditionalInformationInUnitEditor } from '../../../utils/facilityPowerAdditionalInformation';
import {
  POWER_DISTRIBUTION_STAGE_GATE_MESSAGE,
  shouldShowPowerDistributionSection,
} from '../../../utils/facilityPowerScopeSections';
import { resolveEnabledStepIds } from '../../../utils/resolveRequestFormSteps';
import {
  DescriptorEngineSection,
  type DescriptorInstance,
} from '../../sections/descriptorEngineAdapter';
import { POWER_DISTRIBUTION_DESCRIPTOR } from '../../sections/powerDistribution.descriptor';

import type { PowerDistributionConfigurationsSaveHandle } from './subsections/powerDistributionConfigurationsSaveHandle';
import type { RepeatableInstanceEditFormRenderProps } from '../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { PowerDistributionConfiguration } from '../../../types/powerDistribution';

const PowerDistributionStageGateNotice: React.FC = () => (
  <Text style={powerDistributionSharedStyles.stageGateNotice}>
    {POWER_DISTRIBUTION_STAGE_GATE_MESSAGE}
  </Text>
);

const PowerDistributionInitialLayout: React.FC<
  PowerDistributionInitialProps
> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  showFullDetails = true,
  enabledStepIds: enabledStepIdsProp,
  registerPowerDistributionSaveHandle,
  onPowerDistributionEditorActiveChange,
}) => {
  const requestScope = useRequestScope();
  const enabledStepIds =
    enabledStepIdsProp ??
    requestScope?.enabledStepIds ??
    resolveEnabledStepIds(
      (formData as { enabledStepIds?: string[] | null }).enabledStepIds,
    );
  const requestStage = String(
    formData.sysRequestStage ?? formData.stage ?? 'DRAFT',
  );
  const showPowerDistributionFields = shouldShowPowerDistributionSection({
    stage: requestStage,
  });
  const showAdditionalInformationInEditor =
    shouldShowFacilityPowerAdditionalInformationInUnitEditor({
      step: 'power-distribution',
      enabledStepIds,
      stage: requestStage,
    });

  // #997 Story 6.7 — the create/draft layout runs on the descriptor engine (mirror
  // Large UPS 6.5 / Cooling 6.6). The engine owns the list/edit-session/save/flush/
  // attachment lifecycle (6.1 per-unit scoping + 6.2 temp→real remap now run live);
  // Power Distribution supplies its bespoke view (`PowerDistributionEngineEditFormBody`
  // → `PowerDistributionEditFormContent`, both per-unit carousels) and its proven
  // draft/name/clone semantics (`powerDistributionEngineBindings`) through the
  // engine's per-section seams, so the user-visible editor is unchanged. The draft
  // list stays parent-owned in `formData.powerDistributionConfigurations`.
  const draftPowerDistribution =
    (formData.powerDistributionConfigurations as
      | PowerDistributionConfiguration[]
      | undefined) ?? [];
  const draftInstances = useMemo(
    () =>
      draftPowerDistribution.map(
        unit =>
          powerDistributionToDescriptorInstance(unit) as DescriptorInstance,
      ),
    [draftPowerDistribution],
  );
  const requestIdNum = requestId ? Number.parseInt(requestId, 10) || 0 : 0;
  const handleDraftInstancesChange = useCallback(
    (instances: DescriptorInstance[]) =>
      updateFormData(
        'powerDistributionConfigurations',
        instances.map(instance =>
          descriptorInstanceToPowerDistribution(
            instance,
            requestIdNum,
            POWER_DISTRIBUTION_DESCRIPTOR,
          ),
        ),
      ),
    [updateFormData, requestIdNum],
  );

  // Engine handle → bespoke PowerDistributionConfigurationsSaveHandle
  // (CreateRequest's footer-save/dismiss/scope-removal bridge keeps its existing
  // contract).
  const registerInstanceSaveHandle = useMemo(() => {
    if (!registerPowerDistributionSaveHandle) {
      return undefined;
    }
    return (handle: RepeatableInstanceSaveHandle | null) => {
      if (!handle) {
        registerPowerDistributionSaveHandle(null);
        return;
      }
      const powerDistributionHandle: PowerDistributionConfigurationsSaveHandle =
        {
          isPowerDistributionEditorOpen: handle.isInstanceEditorOpen,
          hasUnsavedPowerDistributionEditor: handle.hasUnsavedInstanceEditor,
          commitOpenPowerDistributionEditor: handle.commitOpenInstanceEditor,
          saveUnsavedPowerDistributionIfNeeded: async () => {
            if (
              !handle.isInstanceEditorOpen() ||
              !handle.hasUnsavedInstanceEditor()
            ) {
              return true;
            }
            return handle.commitOpenInstanceEditor();
          },
          hasPendingPowerDistributionChanges: () =>
            handle.hasPendingListChanges?.() ?? false,
          flushPendingPowerDistributionChanges: async () => {
            await handle.flushPendingListChanges?.();
          },
          tryDismissPowerDistributionEditor: handle.tryDismissInstanceEditor,
          discardOpenPowerDistributionEditor: handle.discardOpenInstanceEditor,
          // Draft list is parent-owned (formData.powerDistributionConfigurations)
          // — scope removal strips it there; the in-memory session reset is the
          // persisted-path concern.
          resetSessionAfterScopeRemoval: () => {},
        };
      registerPowerDistributionSaveHandle(powerDistributionHandle);
    };
  }, [registerPowerDistributionSaveHandle]);

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <PowerDistributionEngineEditFormBody
        {...props}
        showAdditionalInformation={showAdditionalInformationInEditor}
        showFullDetails={showFullDetails}
      />
    ),
    [showAdditionalInformationInEditor, showFullDetails],
  );

  return (
    <View style={powerDistributionInitialStyles.container}>
      <PersistedStepHeader
        title="Power Distribution"
        uomSlot={null}
        actionsSlot={null}
      />

      {showPowerDistributionFields ? (
        <DescriptorEngineSection
          descriptor={POWER_DISTRIBUTION_DESCRIPTOR}
          requestId={requestId}
          isEditing={isEditing || !disabled}
          disabled={disabled}
          requestStage={requestStage}
          showFullDetails={showFullDetails}
          draftInstances={draftInstances}
          onDraftInstancesChange={handleDraftInstancesChange}
          legacyRequestFields={formData}
          attachmentsBySection={attachmentsBySection}
          onAttachFiles={onAttachFiles}
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles}
          onFilesSelected={onFilesSelected}
          offlineMode={offlineMode}
          editBody={editBody}
          createDraft={powerDistributionCreateDraft}
          ensureNameForCreate={powerDistributionEnsureNameForCreate}
          cloneForCreate={powerDistributionCloneForCreate}
          registerInstanceSaveHandle={registerInstanceSaveHandle}
          onInstanceEditorActiveChange={onPowerDistributionEditorActiveChange}
          testID="power-distribution-configurations"
        />
      ) : (
        <PowerDistributionStageGateNotice />
      )}
    </View>
  );
};

const PowerDistributionPersistedLayout: React.FC<
  PowerDistributionPersistedProps
> = ({
  request,
  isEditing,
  editedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
  enabledStepIds: enabledStepIdsProp,
  registerInstanceSaveHandle,
  applyEditedDataPatch,
  powerDistributionConfigurationsDirty = false,
  onPowerDistributionEditorActiveChange,
}) => {
  const requestScope = useRequestScope();
  const enabledStepIds =
    enabledStepIdsProp ??
    requestScope?.enabledStepIds ??
    resolveEnabledStepIds(request.enabledStepIds);
  const permissionStage = String(
    request.stage ?? request.sysRequestStage ?? '',
  );
  const showPowerDistributionFields = shouldShowPowerDistributionSection({
    stage: permissionStage,
  });
  const showAdditionalInformationInEditor =
    shouldShowFacilityPowerAdditionalInformationInUnitEditor({
      step: 'power-distribution',
      enabledStepIds,
      stage: permissionStage,
    });
  const requestIdStr = String(request.id ?? '');
  const fieldDisabled = !isEditing;
  const stage = String(request.stage || request.sysRequestStage || 'DRAFT');
  const isOfflineRequest =
    request.id != null && String(request.id).startsWith('o-');

  const pdfFormData = useMemo(
    () => ({
      ...(request as unknown as Record<string, unknown>),
      ...(isEditing ? editedData : {}),
    }),
    [request, editedData, isEditing],
  );

  // #997 Story 6.7 — the persisted layout runs on the descriptor engine (mirror
  // Large UPS / Cooling). Host-boundary display-list resolution: while the user has
  // Power Distribution edits this session, `editedData.powerDistributionConfigurations`
  // is authoritative (even when empty — deleted-all must not resurrect); otherwise
  // units query > request embed > legacy flat-unit synthesis.
  const { data: queryUnits = [] } = usePowerDistributionConfigurations(
    requestIdStr,
    true,
  );
  const editedUnits = editedData?.powerDistributionConfigurations;
  const effectiveUnits = useMemo((): PowerDistributionConfiguration[] => {
    if (
      isEditing &&
      powerDistributionConfigurationsDirty &&
      Array.isArray(editedUnits)
    ) {
      return editedUnits as PowerDistributionConfiguration[];
    }
    if (queryUnits.length > 0) {
      return queryUnits;
    }
    const embed = pdfFormData.powerDistributionConfigurations;
    if (Array.isArray(embed) && embed.length > 0) {
      return embed as PowerDistributionConfiguration[];
    }
    const legacyUnit = buildLegacyPowerDistributionFromRequestFields(
      pdfFormData,
      requestIdStr,
    );
    return legacyUnit ? [legacyUnit] : [];
  }, [
    isEditing,
    powerDistributionConfigurationsDirty,
    editedUnits,
    queryUnits,
    pdfFormData,
    requestIdStr,
  ]);

  const legacyRequestFields = useMemo(
    () => ({
      ...pdfFormData,
      powerDistributionConfigurations: effectiveUnits,
    }),
    [pdfFormData, effectiveUnits],
  );

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <PowerDistributionEngineEditFormBody
        {...props}
        showAdditionalInformation={showAdditionalInformationInEditor}
        showFullDetails={showFullDetails}
      />
    ),
    [showAdditionalInformationInEditor, showFullDetails],
  );

  return (
    <View style={powerDistributionPersistedStyles.container}>
      <PersistedStepHeader
        title="Power Distribution"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={powerDistributionPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={powerDistributionPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      {showPowerDistributionFields ? (
        <DescriptorEngineSection
          descriptor={POWER_DISTRIBUTION_DESCRIPTOR}
          requestId={requestIdStr}
          isEditing={isEditing}
          // #997 QA-C: keep view-mode rows drillable; mutations stay gated by
          // isEditing/listReadOnly, so do NOT restore fieldDisabled (=!isEditing) here.
          disabled={false}
          requestStage={stage}
          showFullDetails={showFullDetails}
          legacyRequestFields={legacyRequestFields}
          applyEditedDataPatch={applyEditedDataPatch}
          attachmentsBySection={attachmentsBySection}
          onAttachFiles={onAttachFiles}
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles}
          onFilesSelected={onFilesSelected}
          offlineMode={isOfflineRequest || offlineMode}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          editBody={editBody}
          createDraft={powerDistributionCreateDraft}
          ensureNameForCreate={powerDistributionEnsureNameForCreate}
          cloneForCreate={powerDistributionCloneForCreate}
          registerInstanceSaveHandle={registerInstanceSaveHandle}
          onInstanceEditorActiveChange={onPowerDistributionEditorActiveChange}
          testID="power-distribution-configurations"
        />
      ) : (
        <PowerDistributionStageGateNotice />
      )}
    </View>
  );
};

const powerDistributionSharedStyles = StyleSheet.create({
  stageGateNotice: {
    marginHorizontal: Platform.OS === 'web' ? 24 : 16,
    marginVertical: 12,
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
  },
});

const powerDistributionInitialStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

const powerDistributionPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
});

export const PowerDistribution: React.FC<PowerDistributionProps> = props =>
  isPowerDistributionPersistedProps(props) ? (
    <PowerDistributionPersistedLayout {...props} />
  ) : (
    <PowerDistributionInitialLayout {...props} />
  );

PowerDistribution.displayName = 'PowerDistribution';

/** @deprecated Test / caption suites — compose path only */
export const PowerDistributionStep = PowerDistributionInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const PowerDistributionInRequest = PowerDistributionPersistedLayout;
